import { addDays, BodyweightList, CalendarResponse, convertWeight, ExerciseList, MUSCLE_GROUP_NAMES, planText, SplitList, todayIn, WorkoutList, type User } from '@squats/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useUser } from '../auth.tsx';
import { useCachedApi } from '../cache.ts';
import { dayOf, isPlanner, nameOf, PlanBadge, splitOf, togetherText } from '../calendar.tsx';
import { PlannedList } from '../components/Plan.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { VaporSun } from '../components/VaporSun.tsx';
import { formatDayLong, formatMonthDay, formatWeekday } from '../dates.ts';
import { db } from '../log/db.ts';
import { startSession } from '../log/store.ts';
import { calendarPath, recentPath } from '../log/sync.ts';

const sets = (n: number) => `${n} ${n === 1 ? 'set' : 'sets'}`;

export function TodayPage() {
  const user = useUser();
  const today = todayIn(user.timezone);
  const { data: cal, error } = useCachedApi(user.id, calendarPath(today, addDays(today, 6)), CalendarResponse);
  const [, navigate] = useLocation();
  const [choosing, setChoosing] = useState(false);
  const { data: library } = useCachedApi(user.id, '/api/exercises', ExerciseList);
  const exercises = useMemo(() => new Map((library?.exercises ?? []).map((e) => [e.id, e])), [library]);
  // Everyone's workouts today, so you can follow along with whoever you're training with.
  const { data: everyone } = useCachedApi(user.id, `/api/workouts?date=${today}`, WorkoutList);
  // Today's workouts on this device (including ones not uploaded yet).
  const todays = useLiveQuery(
    () =>
      db.workouts
        .where('[userId+date]')
        .equals([user.id, today])
        .filter((w) => !w.deletedAt)
        .toArray(),
    [user.id, today],
  );
  const setCounts = useLiveQuery(async () => {
    const ids = (todays ?? []).map((w) => w.id);
    const sets = ids.length ? await db.sets.where('workoutId').anyOf(ids).filter((s) => !s.deletedAt).toArray() : [];
    return new Map(ids.map((id) => [id, sets.filter((s) => s.workoutId === id).length]));
  }, [todays]);

  if (error && !cal) return <p className="error">{error}</p>;
  if (!cal) return <p className="muted">Loading…</p>;

  const mine = dayOf(cal, user.id, today)!;
  const split = splitOf(cal, mine.splitId);
  const with_ = togetherText(cal, mine);
  const others = cal.users.filter((u) => u.id !== user.id);
  const upcoming = Array.from({ length: 6 }, (_, i) => dayOf(cal, user.id, addDays(today, i + 1))!);
  const open = todays?.find((w) => w.endedAt === null);
  const done = (todays ?? []).filter((w) => w.endedAt !== null);

  // The planner trains with everyone: their workout starts one for each of the others too, with the same exercises.
  const start = async (splitId: string | null, name: string, exerciseIds: string[] = []) => {
    const w = { date: today, splitId, name, exerciseIds };
    const partners = isPlanner(user) ? others.map((u) => ({ id: u.id, splitId: dayOf(cal, u.id, today)?.splitId ?? null })) : [];
    const id = await startSession(user, w, partners);
    navigate(`/workouts/${id}`);
  };

  return (
    <div className="stack-lg">
      <div className="today-hero">
        <div className="grow">
          <p className="muted">{formatDayLong(today)}</p>
          <h1>Hi, {user.displayName}</h1>
        </div>
        <VaporSun />
      </div>

      <section className="card stack today" aria-label="Today's plan">
        {mine.kind === 'split' && (
          <>
            <h2 className="today-title">{split?.name ?? 'Workout'}</h2>
            {split && <p className="muted">{split.muscleGroups.map((g) => MUSCLE_GROUP_NAMES[g]).join(' · ')}</p>}
            <PlannedList planned={mine.exercises} exercises={exercises} units={user.units} />
            {with_ && <p>Training {with_}</p>}
          </>
        )}
        {mine.kind === 'rest' && <h2 className="today-title">Rest day</h2>}
        {mine.kind === 'skip' && <h2 className="today-title">Skipped today</h2>}
        {mine.kind === 'unplanned' && (
          <>
            <h2 className="today-title">No plan yet</h2>
            <p>
              <Link href="/plan/setup">Set up your splits and schedule</Link> and today’s workout shows up here.
            </p>
          </>
        )}

        {done.map((w) => (
          <Link key={w.id} href={`/workouts/${w.id}`} className="done-row">
            <span className="pill ok">Done</span> {w.name} · {sets(setCounts?.get(w.id) ?? 0)}
          </Link>
        ))}

        {open ? (
          <Link href={`/workouts/${open.id}`} className="btn block primary">
            Resume {open.name} · {sets(setCounts?.get(open.id) ?? 0)}
          </Link>
        ) : mine.kind === 'split' && done.length === 0 ? (
          <button type="button" className="btn block primary" onClick={() =>
              void start(
                mine.splitId,
                split?.name ?? 'Workout',
                mine.exercises.map((e) => e.exerciseId),
              )
            }>
            Start workout
          </button>
        ) : (
          <button type="button" className="btn block" onClick={() => setChoosing(true)}>
            {done.length ? 'Start another workout' : 'Start a workout anyway'}
          </button>
        )}
      </section>

      {others.length > 0 && (
        <section className="card stack" aria-labelledby="others-h">
          <h2 id="others-h">Everyone today</h2>
          <ul className="list">
            {others.map((u) => {
              const d = dayOf(cal, u.id, today)!;
              const t = togetherText(cal, d);
              const theirs = everyone?.workouts.filter((w) => w.userId === u.id) ?? [];
              return (
                <li key={u.id} className="list-row">
                  <span className="grow row-title">{nameOf(cal, u.id)}</span>
                  <PlanBadge cal={cal} day={d} />
                  {t && <span className="muted small">{t.replace(`with ${user.displayName}`, 'with you')}</span>}
                  {theirs.map((w) => (
                    <Link key={w.id} href={`/workouts/${w.id}`} className="small their-workout">
                      {w.endedAt === null ? 'Training now' : 'Done'}: {w.name} · {sets(w.setCount)} ›
                    </Link>
                  ))}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="card stack" aria-labelledby="next-h">
        <div className="row">
          <h2 id="next-h" className="grow">
            Coming up
          </h2>
          <Link href="/plan" className="small">
            Full week
          </Link>
        </div>
        <ul className="list">
          {upcoming.map((d) => (
            <li key={d.date} className="list-row compact upcoming-row">
              <span className="day-name">{formatWeekday(d.date)}</span>
              <span className="grow">
                <PlanBadge cal={cal} day={d} />
              </span>
              {d.together.length > 0 && <span className="muted small">{togetherText(cal, d)}</span>}
              {d.exercises.length > 0 && (
                <span className="planned-names muted small">{d.exercises.map((e) => `${exercises.get(e.exerciseId)?.name ?? 'Exercise'} ${planText(e, exercises.get(e.exerciseId)?.type ?? 'weight_reps', user.units)}`).join(' · ')}</span>
              )}
            </li>
          ))}
        </ul>
      </section>

      <RecentWorkouts user={user} />

      <BodyweightCard user={user} />

      <Sheet open={choosing} onClose={() => setChoosing(false)} title="Start a workout">
        <ChooseWorkout user={user} onPick={(splitId, name, exerciseIds) => void start(splitId, name, exerciseIds)} />
      </Sheet>
    </div>
  );
}

/** Pick one of your splits, or a workout without one. */
/** Pick one of your splits (its template's exercises come along), or a workout without one. */
function ChooseWorkout({ user, onPick }: { user: User; onPick: (splitId: string | null, name: string, exerciseIds: string[]) => void }) {
  const { data } = useCachedApi(user.id, '/api/splits', SplitList);
  return (
    <ul className="list">
      {(data?.splits ?? []).map((s) => (
        <li key={s.id}>
          <button type="button" className="list-row pick-row" onClick={() => onPick(s.id, s.name, s.exercises.map((e) => e.exerciseId))}>
            <span className="grow stack-sm">
              <span className="row-title">{s.name}</span>
              <span className="muted small">{s.muscleGroups.map((g) => MUSCLE_GROUP_NAMES[g]).join(' · ')}</span>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        </li>
      ))}
      <li>
        <button type="button" className="list-row pick-row" onClick={() => onPick(null, 'Workout', [])}>
          <span className="grow row-title">Something else</span>
          <span aria-hidden="true">›</span>
        </button>
      </li>
    </ul>
  );
}

function RecentWorkouts({ user }: { user: User }) {
  const { data } = useCachedApi(user.id, recentPath(user.id), WorkoutList);
  const list = data?.workouts.filter((w) => w.endedAt !== null).slice(0, 5) ?? [];
  if (!list.length) return null;
  return (
    <section className="card stack" aria-labelledby="recent-h">
      <h2 id="recent-h">Recent workouts</h2>
      <ul className="list">
        {list.map((w) => (
          <li key={w.id}>
            <Link href={`/workouts/${w.id}`} className="list-row compact link-row">
              <span className="day-name">{formatMonthDay(w.date)}</span>
              <span className="grow row-title">{w.name}</span>
              <span className="muted small">
                {w.exerciseCount} ex · {sets(w.setCount)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Latest bodyweight and a link to log today's. Private, so only ever your own. */
function BodyweightCard({ user }: { user: User }) {
  const { data } = useCachedApi(user.id, '/api/bodyweight', BodyweightList);
  const latest = data?.entries.at(-1);
  return (
    <Link href="/bodyweight" className="card row link-row bodyweight-card">
      <span className="grow stack-sm">
        <span className="row-title">Bodyweight</span>
        <span className="muted small">
          {latest ? `${Math.round(convertWeight(latest.weight, latest.unit, user.units) * 10) / 10} ${user.units} on ${formatMonthDay(latest.date)}` : 'Not logged yet'}
        </span>
      </span>
      <span className="small">{latest?.date === todayIn(user.timezone) ? 'View' : 'Log today'} ›</span>
    </Link>
  );
}
